import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCircle2, XCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (type) => {
  if (type === 'listing_approved') return { icon: CheckCircle2, color: '#2E5C41', bg: '#D1FAE5' };
  if (type === 'listing_rejected') return { icon: XCircle, color: '#991B1B', bg: '#FEE2E2' };
  return { icon: Bell, color: '#5C5C5C', bg: '#F2EFEB' };
};

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed');
    }
  };

  const markOneRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/mark-read`, { id }, { withCredentials: true });
      setNotifications(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error) {
      // ignore
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell size={28} strokeWidth={1.5} style={{ color: '#D95A41' }} />
            <h1
              className="text-4xl tracking-tight"
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
              data-testid="notifications-heading"
            >
              Alerts
            </h1>
          </div>
          {unreadCount > 0 && (
            <Button
              onClick={markAllRead}
              variant="ghost"
              className="rounded-md"
              style={{ fontFamily: 'Manrope', fontWeight: 500, color: '#D95A41' }}
              data-testid="mark-all-read-btn"
            >
              <Check size={14} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-center py-12" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>Loading...</p>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid="empty-notifications">
            <Bell size={40} strokeWidth={1.5} style={{ color: '#5C5C5C', margin: '0 auto 12px' }} />
            <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
              You have no notifications yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-list">
            {notifications.map((n) => {
              const { icon: Icon, color, bg } = iconFor(n.type);
              const wrapper = (
                <div
                  className="flex items-start gap-4 p-4 rounded-md border border-[#E5E0D8]"
                  style={{
                    backgroundColor: n.read ? '#FFFFFF' : '#FFF9EC',
                    borderLeft: n.read ? '1px solid #E5E0D8' : `3px solid ${color}`
                  }}
                  data-testid={`notification-${n.id}`}
                >
                  <div
                    className="rounded-full p-2 flex-shrink-0"
                    style={{ backgroundColor: bg, color }}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3
                        style={{
                          fontFamily: 'Manrope',
                          fontWeight: 600,
                          fontSize: '15px',
                          color: '#1A1A1A'
                        }}
                      >
                        {n.title}
                      </h3>
                      {!n.read && (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: '#D95A41' }}
                          data-testid={`unread-dot-${n.id}`}
                        />
                      )}
                    </div>
                    <p style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C', lineHeight: '1.5' }}>
                      {n.message}
                    </p>
                    <p className="mt-2" style={{ fontFamily: 'Manrope', fontSize: '11px', color: '#9CA3AF' }}>
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );

              return n.link ? (
                <Link key={n.id} to={n.link} onClick={() => !n.read && markOneRead(n.id)}>
                  {wrapper}
                </Link>
              ) : (
                <div key={n.id} onClick={() => !n.read && markOneRead(n.id)}>
                  {wrapper}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
